/**
 * Hook for managing local-first configuration
 *
 * Provides state management and API integration for local-first
 * service preferences (local_only, local_first, cloud_first, cloud_only).
 */

import { useCallback, useEffect, useState } from 'react';

import {
  getLocalFirstConfig,
  updateServicePreference,
  updateAutoStart,
} from '../services/localFirstService';
import type { LocalFirstConfig, PreferenceMode } from '../services/localFirstService';

import { handleError } from '../utils/errorHandler';

export interface UseLocalFirstConfigResult {
  /** Current configuration or null if loading/error */
  config: LocalFirstConfig | null;
  /** Whether the config is currently being loaded */
  loading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Update preference for a specific service */
  updatePreference: (
    service: string,
    mode: PreferenceMode,
    fallbackEnabled: boolean
  ) => Promise<void>;
  /** Update the auto-start setting */
  setAutoStart: (enabled: boolean) => Promise<void>;
  /** Refresh the configuration from the server */
  refresh: () => Promise<void>;
}

/**
 * Hook to manage local-first configuration settings
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { config, loading, error, updatePreference } = useLocalFirstConfig();
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *
 *   return (
 *     <select
 *       value={config.llm.mode}
 *       onChange={(e) => updatePreference('llm', e.target.value, config.llm.fallbackEnabled)}
 *     >
 *       <option value="local_first">Local First</option>
 *       <option value="cloud_first">Cloud First</option>
 *     </select>
 *   );
 * }
 * ```
 */
export function useLocalFirstConfig(): UseLocalFirstConfigResult {
  const [config, setConfig] = useState<LocalFirstConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getLocalFirstConfig();
      setConfig(data);
    } catch (e) {
      console.error('[useLocalFirstConfig] Failed to fetch config:', e);
      setError(handleError(e, 'LocalFirstConfig', 'Failed to fetch configuration'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updatePreference = useCallback(
    async (service: string, mode: PreferenceMode, fallbackEnabled: boolean) => {
      try {
        await updateServicePreference(service, mode, fallbackEnabled);
        // Optimistically update local state
        setConfig((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            [service]: {
              ...(prev[service as keyof LocalFirstConfig] as object),
              mode,
              fallbackEnabled,
            },
          } as LocalFirstConfig;
        });
      } catch (e) {
        console.error('[useLocalFirstConfig] Failed to update preference:', e);
        // Refresh to get server state on error
        await fetchConfig();
        throw e;
      }
    },
    [fetchConfig]
  );

  const setAutoStart = useCallback(
    async (enabled: boolean) => {
      try {
        await updateAutoStart(enabled);
        // Optimistically update local state
        setConfig((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            autoStartComponents: enabled,
          };
        });
      } catch (e) {
        console.error('[useLocalFirstConfig] Failed to update auto-start:', e);
        // Refresh to get server state on error
        await fetchConfig();
        throw e;
      }
    },
    [fetchConfig]
  );

  return {
    config,
    loading,
    error,
    updatePreference,
    setAutoStart,
    refresh: fetchConfig,
  };
}
